import React, { useEffect, useCallback, useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { UserContext } from 'context';

import get from 'lodash/get';
import i18n from 'i18n-js';
import moment from 'moment';
import Can from 'components/Can';
import { useConfirm } from 'hooks';
import Title from 'components/Title';
import InvoiceCard from './InvoiceCard';
import PaymentsTable from './PaymentsTable';
import AddIcon from '@mui/icons-material/Add';
import { paperSx, textColorSx, linkClassName, linkHostSx } from './Invoice.styled';
import InvoiceItemsTable from './InvoiceItemsTable';
import { formatAmount, getDynamicLink, buildUrl } from 'utils';
import { InvoiceActions } from 'state/modules/invoice';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { AddStripePaymentMethodModal, InvoiceItemModal, AddCreditsModal } from 'components/Modals';
import { PaymentMethodsSelect } from 'components/AsyncFields';
import { FETCH_STATE } from 'constants';
import ItemStatus from 'components/ItemStatus';
import { Link } from 'react-router-dom';

const InvoiceItem = ({ title, description, fontSize, fontWeight, extra }) => {
  return (
    <Box display="flex" alignItems="center">
      <Typography component="span" sx={textColorSx}>
        <Box fontSize={fontSize || 13} component="span" fontWeight="fontWeightBold">
          {title}:
        </Box>
      </Typography>
      <Typography component="span">
        <Box
          ml={1}
          component="span"
          fontSize={fontSize || 13}
          fontWeight={fontWeight || 'fontWeightRegular'}
        >
          {description || '--'}
        </Box>
      </Typography>
      {extra && extra}
    </Box>
  );
};

InvoiceItem.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  fontSize: PropTypes.number,
  fontWeight: PropTypes.string,
  extra: PropTypes.node,
};

const InvoicePayments = ({ invoice, loading, total_paid }) => (
  <>
    <Grid size={12}>
      <Typography component="span">
        <Box mb={2} fontSize={13} component="span" fontWeight="fontWeightBold">
          {i18n.t('invoice.payment_information')}
        </Box>
      </Typography>
    </Grid>
    <Grid size={12}>
      <PaymentsTable loading={loading} payments={invoice?.payments || []} />
    </Grid>
    <Grid size={12}>
      <Box display="flex" mt={2} justifyContent="flex-end">
        <InvoiceCard
          list={[
            {
              label: i18n.t('invoice.paid_with_credits'),
              value: '$' + formatAmount(invoice?.paid_credit),
            },
            {
              label: i18n.t('invoice.total_preauth'),
              value: '$' + formatAmount(invoice?.pre_auth),
            },
            {
              label: i18n.t('invoice.total_paid'),
              value: '$' + formatAmount(total_paid),
            },
          ]}
        />
      </Box>
    </Grid>
  </>
);

InvoicePayments.propTypes = {
  invoice: PropTypes.object,
  loading: PropTypes.bool,
  total_paid: PropTypes.number,
};

const InvoicePreview = ({ id, isBill, isStatementEntry }) => {
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const { orgType } = useContext(UserContext);

  // Component state
  const [openModal, setOpenModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [openCreditModal, setOpenCreditModal] = useState(false);
  const [openAddPaymentMethod, setOpenAddPaymentMethod] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);

  // Redux State
  const invoice = useSelector((state) => state.invoice.invoice);
  const { user } = useSelector((state) => state.users);
  const loadingInvoice = useSelector((state) => state.invoice.loading);
  const fetchingInvoice = useSelector((state) => state.invoice.fetchingInvoice);
  const fetchingInvoiceItem = useSelector((state) => state.invoice.fetchingInvoiceItem);
  const fetchingExportInvoice = useSelector((state) => state.invoice.fetchingExportInvoice);
  const localeFormat = useSelector((state) => state.organizations.localeFormat);
  const organization = useSelector((state) => state.organizations.organization);
  const accountEmitter = useSelector((state) => state.invoice.accountEmitter);
  const fetchingAccountEmitter = useSelector((state) => state.invoice.fetchingAccountEmitter);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const currentOrg = useSelector((state) => state.auth?.organizations?.current);

  // Const derived form component/redux state
  const accountId = organization?.account_id;
  const invoiceEmitterId = invoice?.account_customer?.holder_id;
  const holderIsService = invoice?.account_customer?.holder_type === 'ServiceOrganization';
  const loading =
    loadingInvoice ||
    fetchingInvoice === FETCH_STATE.LOADING ||
    (isBill &&
      (fetchingAccountEmitter === FETCH_STATE.LOADING || invoiceEmitterId !== accountEmitter?.id));

  const total_paid =
    invoice?.payments?.reduce((accumulator, payment) => accumulator + payment.charge_amount, 0) ||
    0;
  const showItemDetailsLink = sharingType === 'FFB';

  const { source, source_id } =
    invoice?.source_name === 'SubscriptionPeriod'
      ? {
          source: invoice?.extra_source_name,
          source_id: invoice?.extra_source_id,
        }
      : { source: invoice?.source_name, source_id: invoice?.source_id };

  const showProcessButton = sharingType !== 'FFB';

  /**
   * Toggle confirm modal
   * @param {string} item Id of the item
   */
  const handleDeleteItem = (item) => {
    setCurrentItem(item);
    toggleDeleteItem();
  };

  /**
   * Toggle item modal
   * @param {item} item Current invoice item
   */
  const handleClickModal = (item) => {
    setCurrentItem(item);
    setOpenModal((prevValue) => !prevValue);
  };

  /**
   * Toggle credits modal
   */
  const handleCreditModal = () => {
    setOpenCreditModal((prevState) => !prevState);
  };

  const [toggleDeleteItem, DeleteItemConfirm] = useConfirm({
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    title: i18n.t('invoice.delete_item'),
    message: i18n.t('invoice.delete_item_message'),
    onConfirm: useCallback(() => {
      dispatch(InvoiceActions.fetchDeleteItemFromInvoice(currentItem));
    }, [dispatch, currentItem]),
  });

  // Fetch invoice on outdated data
  useEffect(() => {
    if (id && fetchingInvoice === FETCH_STATE.UNSENT && isMounted.current) {
      dispatch(InvoiceActions.fetchInvoiceById(id));
    }
  }, [dispatch, id, fetchingInvoice]);

  // Fetch invoice on load
  useEffect(() => {
    if (id) {
      dispatch(InvoiceActions.fetchInvoiceById(id));
      isMounted.current = true;
    }
  }, []);

  useEffect(() => {
    if (isBill && invoiceEmitterId !== accountEmitter?.id) {
      dispatch(InvoiceActions.fetchAccountEmitterById(invoiceEmitterId));
    }
  }, [dispatch, isBill, invoiceEmitterId]);

  const handleAddPaymentMethodModalOpen = () => {
    setOpenAddPaymentMethod((prevState) => !prevState);
  };

  const onProcessBalance = () => {
    if (isBill) {
      dispatch(
        InvoiceActions.fetchProcessInvoiceById(
          id,
          paymentMethod ? { payment_method_id: paymentMethod } : undefined
        )
      );
    } else {
      dispatch(InvoiceActions.fetchProcessInvoiceById(id));
    }
  };

  const exportInvoice = () => {
    dispatch(InvoiceActions.fetchExportInvoice(id));
  };

  const onPublishInvoice = () => {
    dispatch(InvoiceActions.fetchPublishInvoice());
  };

  const handleApproval = (id, status) => {
    dispatch(InvoiceActions.fetchUpdateInvoiceApproval(id, status));
  };

  const [toggleSendInvoice, SendInvoice] = useConfirm({
    title: i18n.t('invoice.submit_title'),
    message: i18n.t('invoice.submit_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.submit'),
    onConfirm: onPublishInvoice,
  });

  const isOwner = orgType === 'msOwnerOrg';
  const isInvoiceStatement = invoice?.receivable_type === 'statement';
  const isInternalInvoice = invoice?.internal;
  const showPaymentControls = !isInvoiceStatement;
  const showAcceptRejectControls =
    invoice?.is_draft === false &&
    invoice?.rendered_time === null &&
    isInvoiceStatement &&
    isInternalInvoice &&
    !isOwner;
  const showSubmitButton =
    invoice?.is_draft === true && currentOrg?.id === invoice?.account_emitter?.holder_id;
  const showAssociatedAccountStatement = invoice?.account_statement?.id;

  return (
    <Grid container spacing={3}>
      <Grid size={12}>
        <Grid container spacing={3}>
          <Grid style={{ marginRight: 'auto' }}>
            <Title
              title={
                isStatementEntry
                  ? i18n.t('statement_entries.details')
                  : isBill
                    ? i18n.t('bill.details')
                    : i18n.t('invoice.detail')
              }
            />
          </Grid>

          {isBill && showAcceptRejectControls ? (
            <>
              <Grid>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    handleApproval(invoice.id, 'REJECT');
                  }}
                  style={{ textTransform: 'capitalize' }}
                >
                  {i18n.t('common.reject')}
                </Button>
              </Grid>
              <Grid>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    handleApproval(invoice.id, 'APPROVE');
                  }}
                  style={{ textTransform: 'capitalize' }}
                >
                  {i18n.t('common.approve')}
                </Button>
              </Grid>
            </>
          ) : null}
          {showSubmitButton && (
            <Grid>
              <Button
                color="primary"
                variant="outlined"
                onClick={toggleSendInvoice}
                style={{ textTransform: 'capitalize' }}
              >
                {i18n.t('common.submit')}
              </Button>
            </Grid>
          )}
          {isBill && showPaymentControls ? (
            <Grid>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddPaymentMethodModalOpen}
                style={{ textTransform: 'capitalize' }}
              >
                {i18n.t('bill.add_payment')}
              </Button>
            </Grid>
          ) : null}
          {isBill ? (
            <Grid>
              <Button
                variant="outlined"
                onClick={exportInvoice}
                color="primary"
                disabled={fetchingExportInvoice}
                data-cy="bill-download-btn"
                style={{ textTransform: 'capitalize' }}
              >
                {i18n.t('common.download')}
              </Button>
            </Grid>
          ) : null}
          <Grid>
            {showProcessButton &&
              get(invoice, 'invoiced', 0) -
                get(invoice, 'paid_card', 0) -
                get(invoice, 'paid_credit', 0) !==
                0 && (
                <Can I="process" a="receivable">
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={onProcessBalance}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {i18n.t('invoice.process_balance')}
                  </Button>
                </Can>
              )}
          </Grid>
        </Grid>
      </Grid>
      {isBill && showPaymentControls ? (
        <Grid size={12}>
          <Grid container spacing={3}>
            <Grid size={4} style={{ marginRight: 'auto' }}>
              <PaymentMethodsSelect
                field={{ value: paymentMethod || '' }}
                form={{ setFieldValue: (_, val) => setPaymentMethod(val) }}
                accountId={accountId}
                acceptedPaymentTypes={invoice?.account_emitter?.payment_methods_allowed || []}
              />
            </Grid>
          </Grid>
        </Grid>
      ) : null}
      <Grid size={12}>
        <Paper elevation={2} sx={[paperSx, linkHostSx]}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Box display="flex" justifyContent="space-between">
                <Typography component="span">
                  <Box fontSize={20} component="span" fontWeight="fontWeightBold">
                    {isStatementEntry ? (
                      i18n.t('statement_entries.preview_title', { id })
                    ) : (
                      <>
                        {i18n.t('invoice.invoice')} {i18n.t('common.id')}
                        {' #'}
                        {id}
                      </>
                    )}
                  </Box>
                </Typography>
                {invoice?.status && (
                  <ItemStatus i18nKey="invoice_statuses" status={invoice?.status} />
                )}
              </Box>
            </Grid>
            <Grid size={12}>
              <Box display="flex" flexWrap="wrap" justifyContent="space-between">
                <Box display="flex" flexDirection="column">
                  <InvoiceItem
                    title={i18n.t('invoice.from')}
                    description={invoice?.account_emitter?.customer_name}
                  />
                  <InvoiceItem
                    title={i18n.t('invoice.to')}
                    description={
                      isBill
                        ? invoice?.account_customer?.customer_name
                        : getDynamicLink(
                            invoice?.account_customer?.customer_name,
                            invoice?.account_customer?.holder_id,
                            'Driver',
                            linkClassName
                          )
                    }
                    extra={
                      isBill || holderIsService ? null : (
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={handleCreditModal}
                          style={{ textTransform: 'inherit', marginLeft: 8 }}
                          data-cy="add-credits-btn"
                        >
                          {i18n.t('payments.add_credit')}
                        </Button>
                      )
                    }
                  />
                  <InvoiceItem
                    title={i18n.t('invoice.source')}
                    description={
                      isBill
                        ? invoice?.description?.replace('VehicleGroup', 'Fleet')
                        : getDynamicLink(
                            `${source} ID#${source_id}`,
                            source_id,
                            source,
                            linkClassName,
                            undefined,
                            invoice?.description
                          )
                    }
                  />
                  <InvoiceItem
                    title={
                      isInvoiceStatement
                        ? i18n.t('invoice.due_date')
                        : i18n.t('invoice.due_datetime')
                    }
                    description={
                      moment(invoice?.due_time).isValid()
                        ? isInvoiceStatement
                          ? moment(invoice?.due_time).utc(false).format(localeFormat)
                          : moment(invoice?.due_time).format(`${localeFormat}  hh:mm A`)
                        : null
                    }
                  />
                  {showAssociatedAccountStatement ? (
                    <InvoiceItem
                      title={i18n.t('account_statement.invoice_ref', {
                        status:
                          invoice?.account_statement?.status?.charAt(0).toUpperCase() +
                          invoice?.account_statement?.status?.toLowerCase().slice(1),
                      })}
                      description={
                        <Link
                          to={buildUrl(`/account-statement/${invoice?.account_statement?.id}`)}
                          className={linkClassName}
                        >
                          {`#${invoice?.account_statement?.id}`}
                        </Link>
                      }
                    />
                  ) : null}
                  {invoice?.processed_time && (
                    <InvoiceItem
                      title={i18n.t('invoice.processed_datetime')}
                      description={
                        moment(invoice?.processed_time).isValid()
                          ? moment(invoice?.processed_time).format(`${localeFormat} hh:mm A`)
                          : null
                      }
                    />
                  )}
                </Box>
                <Box display="flex" flexDirection="column" alignItems="flex-end">
                  <InvoiceItem
                    title={i18n.t(
                      isStatementEntry ? 'common.total_amount' : 'invoice.total_invoice'
                    )}
                    description={formatAmount(invoice?.invoiced)}
                  />
                  {!isStatementEntry && (
                    <InvoiceItem
                      title={i18n.t('invoice.total_paid')}
                      description={formatAmount(total_paid)}
                    />
                  )}
                  {!isStatementEntry && (
                    <InvoiceItem
                      fontSize={20}
                      fontWeight="fontWeightBold"
                      title={i18n.t('invoice.amount_due')}
                      description={formatAmount(
                        (invoice?.invoiced || 0) -
                          (invoice?.paid_card || 0) -
                          (invoice?.paid_credit || 0)
                      )}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid size={12}>
              <Typography component="span">
                <Box mb={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                  {i18n.t(isStatementEntry ? 'statement_entries.items' : 'invoice.invoice_items')}
                </Box>
              </Typography>
            </Grid>
            <Grid size={12}>
              <InvoiceItemsTable
                loading={loading}
                handleEditItem={handleClickModal}
                handleCreateItem={handleClickModal}
                handleDeleteItem={handleDeleteItem}
                invoice={invoice || {}}
                isBill={isBill}
                showItemDetailsLink={showItemDetailsLink}
              />
            </Grid>
            <Grid size={12}>
              <Box display="flex" mt={-5.5} justifyContent="flex-end">
                <InvoiceCard
                  list={[
                    {
                      label: i18n.t('invoice.subtotal'),
                      value: '$' + formatAmount((invoice?.invoiced || 0) - (invoice?.tax || 0)),
                    },
                    {
                      label: i18n.t('invoice.total_tax'),
                      value: '$' + formatAmount(invoice?.tax),
                    },
                    {
                      label: i18n.t(
                        isStatementEntry ? 'common.total_amount' : 'invoice.total_invoice'
                      ),
                      value: '$' + formatAmount(invoice?.invoiced),
                    },
                  ]}
                />
              </Box>
            </Grid>
          </Grid>
          {isBill ? null : (
            <InvoicePayments invoice={invoice} loading={loading} total_paid={total_paid} />
          )}
          <DeleteItemConfirm />
          {openModal ? (
            <InvoiceItemModal
              invoice={id}
              open={openModal}
              current={currentItem}
              loading={fetchingInvoiceItem}
              handleClick={handleClickModal}
            />
          ) : null}
          {openCreditModal ? (
            <AddCreditsModal
              to="driver"
              type="credit"
              accountLevel={true}
              user={user}
              open={openCreditModal}
              handleClick={handleCreditModal}
              account={invoice.account_customer}
            />
          ) : null}
          {openAddPaymentMethod ? (
            <AddStripePaymentMethodModal
              open={true}
              handleClick={handleAddPaymentMethodModalOpen}
              accountId={accountId}
            />
          ) : null}
        </Paper>
      </Grid>
      <SendInvoice />
    </Grid>
  );
};

InvoicePreview.propTypes = {
  id: PropTypes.any,
  isBill: PropTypes.bool,
  isStatementEntry: PropTypes.bool,
};

export { InvoicePreview };
